import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { QuestionBankData } from "../../../../../../types/question-bank";
import {
  Button,
  Flex,
  message,
  Modal,
  Select,
  Steps,
  TableColumnsType,
  TableProps,
} from "antd";
import useDebounce from "../../../../../../hooks/useDebounce";
import {
  searchQuestionBanks,
  searchQuizInQuestionBank,
} from "../../../../../../service/question-bank";
import TableData from "../../../../../../components/table-data/TableData";
import { renderQuizType } from "../../../../../../utils/format";
import QuizPreview from "../../../../../../components/quiz-preview/QuizPreview";
import {
  EyeOutlined,
  LeftOutlined,
  RightOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { convertLatexToImage, stripHtml } from "../../../../../../utils/app";
import BankQuestion from "../../../../../library/bank-question";
import useKeyword from "../../../../../../hooks/useKeyword";

interface BankQuestionsModalProps {
  open?: boolean;
  modalMode?: string;
  onClose?: () => void;
  courseBlockGroupIdClick: number;
  onOk?: (selectedQuiz: any) => void;
}

function BankQuestionsModal({
  open,
  modalMode,
  courseBlockGroupIdClick,
  onOk = () => {},
  onClose = () => {},
}: BankQuestionsModalProps) {
  const { t } = useTranslation();
  const [questionBanks, setQuestionBanks] = useState<QuestionBankData[]>([]);
  const [questionBankSelected, setQuestionBankSelected] = useState<
    number | null
  >(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any>([]);
  const [questions, setQuestions] = useState<any>([]);
  const [statusOrder, setStatusOrder] = useState<string>("");
  const [fieldSortOrder, setFieldSortOrder] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [changeVersionLoading, setChangeVersionLoading] =
    useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>("");
  const keyWordDebounce = useDebounce(keyword, 300);

  const [isOpenQuizPreviewModal, setIsOpenQuizPreviewModal] =
    useState<boolean>(false);
  const [quizDetailId, setQuizDetailId] = useState<number>(0);
  const [showUsedTable, setShowUsedTable] = useState<boolean>(false);

  const [newListQuestions, setNewListQuestions] = useState<any>([]);

  const currentCourseBlockGroupId =
    useKeyword("courseBlockGroupIdClick") || courseBlockGroupIdClick;
  const unitId = useKeyword("unitId");
  const mode = useKeyword("mode");
  const blockId = useKeyword("blockId");

  // Thay đổi câu hỏi theo version được chọn từ modal preview
  const handleSaveQuizPreviewModal = () => {
    if (newListQuestions.length > 0) {
      setQuizDetailId(0);
      setQuestions(newListQuestions);
      message.success(t("Thay đổi phiên bản thành công", "Thay đổi phiên bản thành công"));
    }
    setIsOpenQuizPreviewModal(false);
  };

  useEffect(() => {
    const getQuestionBanks = async () => {
      const res = await searchQuestionBanks({
        page: 1,
        size: 50,
        keyword: keyWordDebounce,
        isShared: null,
      });
      setQuestionBanks(res.data.data.content);
    };
    getQuestionBanks();
  }, [keyWordDebounce]);

  useEffect(() => {
    const getQuestions = async () => {
      if (questionBankSelected) {
        setLoading(true);
        const res = await searchQuizInQuestionBank({
          page: pageNumber,
          size: pageSize,
          sort: statusOrder ? [`${fieldSortOrder},${statusOrder}`] : [],
          bankId: questionBankSelected,
          includingDraft: false,
        });
        setQuestions(res.data?.data?.content);
        setTotalRecords(res.data?.data?.totalElements);
        setLoading(false);
      }
    };
    getQuestions();
  }, [pageNumber, pageSize, questionBankSelected, statusOrder, fieldSortOrder]);

  const dataColumns: TableColumnsType<any> = [
    {
      title: "STT",
      dataIndex: "numericalOrder",
      key: "numericalOrder",
      sorter: true,
      width: "10%",
      render: (_: any, record, index: number) => {
        return <span>{record?.numericalOrder}</span>;
      },
    },
    {
      title: t("Câu hỏi", "Câu hỏi"),
      dataIndex: "question",
      key: "question",
      width: "40%",
      sorter: true,
      className: "question-col",
      render: (_: any, record, index: number) => {
        return (
          <div
            className="question-content"
            dangerouslySetInnerHTML={{
              __html: `${convertLatexToImage(record?.question)}`,
            }}
          ></div>
        );
      },
    },
    {
      title: t("Phiên bản", "Phiên bản"),
      dataIndex: "questionBankVersion",
      key: "questionBankVersion",
      width: "10%",
    },
    {
      title: t("Loại câu hỏi", "Loại câu hỏi"),
      dataIndex: "type",
      key: "type",
      sorter: true,
      width: "25%",
      render: (_: any, record, index: number) => {
        return <div>{renderQuizType(record?.type)}</div>;
      },
    },
    {
      key: "action",
      width: "10%",
      render: (_: any, record, index: number) => {
        return (
          <Button
            onClick={() => {
              setShowUsedTable(false);
              setQuizDetailId(record?.id);
              setIsOpenQuizPreviewModal(true);
            }}
          >
            <EyeOutlined />
          </Button>
        );
      },
    },
  ];

  const handleTableChange: TableProps["onChange"] = (
    pagination,
    filters,
    sorter: any
  ) => {
    const data =
      sorter.order === "ascend"
        ? "question,asc"
        : sorter.order === "descend"
        ? "question,desc"
        : "";
    setStatusOrder(data);
    setFieldSortOrder(sorter.field);
  };

  const [current, setCurrent] = useState(0);
  const [selectedBank, setSelectedBank] = useState<QuestionBankData[]>([]);

  const steps = [
    {
      title: t("Chọn ngân hàng", "Chọn ngân hàng"),
      content: (
        <BankQuestion
          mode={mode}
          selectedMode
          unitId={unitId}
          blockId={blockId}
          modalMode="bank-select"
          selectedBank={selectedBank}
          courseBlockGroupIdClick={courseBlockGroupIdClick}
          setSelectedBank={setSelectedBank}
        />
      ),
    },
    {
      title: t("Chọn câu hỏi", "Chọn câu hỏi"),
      content: (
        <TableData
          hideSelection={true}
          dataColumns={dataColumns}
          pageNumber={pageNumber}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setPageNumber={setPageNumber}
          loadingTable={loading}
          totalRecords={totalRecords}
          rowSelected={selectedRowKeys}
          setSelectedRowKeysProps={setSelectedRowKeys}
          pagination={true}
          dataTable={questions}
          scroll={
            modalMode === "select" || modalMode === "bank-select"
              ? undefined
              : { x: 600, y: 580 }
          }
          handleTableChangeProps={handleTableChange}
          unit={t("câu hỏi", "câu hỏi")}
        />
      ),
    },
  ];

  // Step
  const next = () => {
    setQuestionBankSelected(
      selectedBank?.length > 0 ? selectedBank?.[0]?.id : null
    );
    setSelectedRowKeys([]);
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const items = steps.map((item) => ({ key: item.title, title: item.title }));

  return (
    <div>
      <Modal
        className="add-type-question-modal select-question-bank-modal"
        title={t("Thêm từ ngân hàng câu hỏi", "Thêm từ ngân hàng câu hỏi")}
        open={open}
        onOk={() => onOk(selectedRowKeys)}
        onCancel={() => {
          onClose();
          setSelectedRowKeys([]);
        }}
        centered
        okText={t("Lưu lại", "Lưu lại")}
        okButtonProps={{ disabled: selectedRowKeys.length === 0 }}
        cancelText={t("Hủy bỏ", "Hủy bỏ")}
        width={"90vw"}
        height={"90vh"}
        footer={null}
        style={{ top: 10 }}
      >
        <div className="list-type-question">
          <Steps current={current} items={items} />
          <div className="step-body">
            <div className="step-body__bank">
              {selectedBank.length === 0 ? null : (
                <>
                  {t("Ngân hàng:", "Ngân hàng:")}{" "}
                  <span style={{ fontStyle: "italic" }}>
                    {selectedBank?.[0]?.name}
                  </span>
                </>
              )}
            </div>
            <div className="step-body__content">{steps[current].content}</div>
          </div>
          <div className="step-footer">
            {current < steps.length - 1 && (
              <Button
                disabled={selectedBank.length === 0}
                type="primary"
                onClick={() => next()}
                icon={<RightOutlined />}
                iconPosition="end"
              >
                {t("Tiếp", "Tiếp")}
              </Button>
            )}
            {current > 0 && (
              <>
                <Button
                  style={{ marginRight: 10 }}
                  onClick={() => prev()}
                  icon={<LeftOutlined />}
                >
                  {t("Quay lại", "Quay lại")}
                </Button>
                <Button
                  disabled={selectedRowKeys.length === 0}
                  type="primary"
                  onClick={() => onOk(selectedRowKeys)}
                  icon={<SaveOutlined />}
                  iconPosition="end"
                >
                  {t("Lưu", "Lưu")}
                </Button>
              </>
            )}
          </div>

          <QuizPreview
            questions={questions}
            quizId={quizDetailId}
            showUsedTable={showUsedTable}
            isOpenModal={isOpenQuizPreviewModal}
            setQuizDetailId={setQuizDetailId}
            setNewListQuestions={setNewListQuestions}
            setIsOpenModal={setIsOpenQuizPreviewModal}
            setChangeVersionLoading={setChangeVersionLoading}
            footer={
              <div style={{ textAlign: "right" }}>
                <Button
                  type="primary"
                  disabled={changeVersionLoading}
                  onClick={handleSaveQuizPreviewModal}
                >
                  {t("Lưu", "Lưu")}
                </Button>
              </div>
            }
          />
        </div>
      </Modal>
    </div>
  );
}

export default BankQuestionsModal;
